import { TREND_AXIS_MODE, TREND_AXIS_MODE_OPTIONS } from '../../../filters/lib/statisticsTrendDimension';
import {
	isPipelineBuiltinChartKind,
	resolveCompositionChartConfigFromSnapshot,
	resolveOverdueChartConfigFromSnapshot,
	resolvePersistedChartKind,
	PERSISTED_CHART_KIND,
} from './statisticsPersistedCharts';
import { getPrimaryPhaseMetricLabel } from '../../pipeline/lib/orderPrimaryPhaseMetric';
import { ORDER_PRIMARY_PHASE_DIMENSION_OPTIONS } from '../../pipeline/lib/orderPrimaryPhaseDimension';
import {
	isTrendBarChart,
	isTrendMapChart,
	TREND_CHART_TYPE,
	TREND_CHART_TYPE_OPTIONS,
	TREND_LINE_SHAPE,
	TREND_LINE_SHAPE_OPTIONS,
} from '../../trend/lib/trendChartConfig';
import { formatTrendYAxisMetricsLabel, normalizeTrendYAxisMetrics } from '../../trend/lib/trendYAxisMetrics';
import { getChartBarOrientationLabel } from '../../shared/lib/barOrientation';
import {
	isCompositionChartBarType,
	getCompositionChartDimensionLabel,
	formatCompositionChartMetricsLabel,
	getCompositionChartTypeLabel,
} from '../../composition/lib/compositionChartConfig';
import { getOverdueChartRangeModeLabel } from '../../overdue/lib/overdueStatChartData';
import {
	getOverdueChartDimensionLabel,
	getOverdueChartMetricLabel,
	getOverdueChartTypeLabel,
} from '../../overdue/lib/overdueChartConfig';

export const SCOPE_TAG_EXCLUDE_IDS = new Set(['createdDateRange', 'completedDateRange']);

export const resolveOptionLabel = (value, options = []) => options.find((item) => item.value === value)?.label ?? value;

export const buildLineCompareCompositionParts = (filters = {}) => {
	const axisMode = filters.trendAxisMode ?? TREND_AXIS_MODE.TIME;
	const chartType = filters.trendChartType ?? TREND_CHART_TYPE.LINE;
	const parts = [];

	if (axisMode !== TREND_AXIS_MODE.TIME) {
		const axisLabel = resolveOptionLabel(axisMode, TREND_AXIS_MODE_OPTIONS);
		parts.push(`横轴${axisLabel}`);
	}

	if (!isTrendMapChart(chartType)) {
		parts.push(formatTrendYAxisMetricsLabel(normalizeTrendYAxisMetrics(filters)));
	}

	parts.push(resolveOptionLabel(chartType, TREND_CHART_TYPE_OPTIONS));

	if (chartType === TREND_CHART_TYPE.LINE) {
		const lineShape = filters.trendLineShape ?? TREND_LINE_SHAPE.SMOOTH;
		parts.push(resolveOptionLabel(lineShape, TREND_LINE_SHAPE_OPTIONS));
	}

	if (isTrendBarChart(chartType)) {
		parts.push(getChartBarOrientationLabel(filters.trendBarOrientation));
	}

	return parts.filter(Boolean);
};

export const buildCompositionChartCompositionParts = (snapshot = {}) => {
	const config = resolveCompositionChartConfigFromSnapshot(snapshot);
	const parts = [
		getCompositionChartDimensionLabel(config.dimension),
		formatCompositionChartMetricsLabel(config.metrics),
		getCompositionChartTypeLabel(config.chartType),
	];
	if (isCompositionChartBarType(config.chartType)) {
		parts.push(getChartBarOrientationLabel(config.barOrientation));
	}
	return parts;
};

export const buildOverdueChartCompositionParts = (snapshot = {}) => {
	const config = resolveOverdueChartConfigFromSnapshot(snapshot);
	return [
		'超期统计',
		getOverdueChartDimensionLabel(config.dimension),
		getOverdueChartMetricLabel(config.metric),
		getOverdueChartTypeLabel(config.chartType),
		getOverdueChartRangeModeLabel(config.rangeMode),
	];
};

export const buildPipelineBuiltinCompositionParts = (
	chartKind,
	{ activeMetric, activeDimension, overdueConfig = null } = {},
) => {
	if (chartKind === PERSISTED_CHART_KIND.PIPELINE_BAR) {
		return ['流程分布', `指标 ${getPrimaryPhaseMetricLabel(activeMetric)}`, '柱状图'];
	}
	if (chartKind === PERSISTED_CHART_KIND.PIPELINE_PIE) {
		return ['指标占比', `指标 ${getPrimaryPhaseMetricLabel(activeMetric)}`, '饼图'];
	}
	if (chartKind === PERSISTED_CHART_KIND.PIPELINE_DIMENSION) {
		const dimensionLabel = resolveOptionLabel(activeDimension, ORDER_PRIMARY_PHASE_DIMENSION_OPTIONS);
		return ['贡献排行', `指标 ${getPrimaryPhaseMetricLabel(activeMetric)}`, `维度 ${dimensionLabel}`, '条形图'];
	}
	if (chartKind === PERSISTED_CHART_KIND.PIPELINE_MAP) {
		return ['客户分布', '地图', '按省份聚合'];
	}
	if (chartKind === PERSISTED_CHART_KIND.OVERDUE && overdueConfig) {
		return buildOverdueChartCompositionParts({ overdueConfig });
	}
	return [];
};

export const resolveSavedChartCompositionParts = (snapshot = {}, filters = null, runtime = null) => {
	const chartKind = resolvePersistedChartKind(snapshot);
	if (chartKind === PERSISTED_CHART_KIND.OVERDUE) {
		return buildOverdueChartCompositionParts(snapshot);
	}
	if (isPipelineBuiltinChartKind(chartKind)) {
		return buildPipelineBuiltinCompositionParts(chartKind, runtime ?? {});
	}
	if (!filters) return [];
	if (chartKind === PERSISTED_CHART_KIND.COMPOSITION) {
		return buildCompositionChartCompositionParts(snapshot);
	}
	return buildLineCompareCompositionParts(filters);
};
